import {
  CSSProperties,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useProductTranslations } from "../../hooks/TranslationHooks";
import classNames from "classnames";
import { Button, Input, InputGroup, InputGroupText } from "reactstrap";
import FormattedPrice from "../FormattedPrice";
import { multiply } from "../../utils/NumberUtils";
import ProductStock from "../ProductStock";
import Icon from "../Icon";
import { Product } from "../../types/Product";
import "./style.scss";
import { debounce } from "lodash";

const CompactPackageCalculator = (props: {
  product: Product;
  m2PerPackage: number;
  pricePerPackage: number;
  pricePerM2?: number;
  priceUnit?: string;
  stock: string;
  currency: string;
  valuePkg?: number;
  valueM2?: number;
  valueCallback: (value: number) => void;
  b2b?: boolean;
  isBusiness?: boolean;
  compact?: boolean;
  short?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) => {
  const {
    id,
    className,
    style,
    product,
    m2PerPackage,
    pricePerPackage,
    pricePerM2,
    priceUnit,
    stock,
    currency,
    valuePkg = 0,
    valueM2 = 0,
    valueCallback,
    b2b,
    isBusiness,
    compact,
    short,
    children
  } = props;
  const tp = useProductTranslations();
  const ref = useRef<HTMLInputElement>(null);
  const stockInt = parseInt(stock) || 999;
  const [m2, setM2] = useState<number | undefined>(
    valuePkg ? multiply(valuePkg, m2PerPackage) : valueM2 ? valueM2 : undefined
  );
  const [pkg, _setPkg] = useState<number>(
    valuePkg ? valuePkg : valueM2 ? Math.ceil(valueM2 / m2PerPackage) : 0
  );
  const setPkg = useCallback(
    (newPkg: number) => {
      _setPkg(newPkg);
      valueCallback?.(newPkg);
    },
    [valueCallback]
  );

  const changePkg = useCallback(
    (value: number) => {
      let newPkg = value;
      if (isNaN(newPkg) || newPkg < 1) {
        newPkg = 1;
      }
      setPkg(newPkg);
      const newM2 = multiply(newPkg, m2PerPackage);
      setM2(newM2);
      if (ref.current) {
        ref.current.value = "" + (newM2 || 0);
        ref.current.select();
      }
    },
    [m2PerPackage, setPkg]
  );

  const changeM2 = useCallback(
    (value: string) => {
      value = value.replace(",", "."); //Handle , as decimal sign
      if (value.substr(-1) === ".") {
        value = value.replace(".", "");
      }
      let newM2: number | undefined = parseFloat(value);
      if (isNaN(newM2) || newM2 <= 0) {
        newM2 = undefined;
      }
      console.log("newM2", newM2);
      setM2(newM2);
      changePkg(Math.ceil((newM2 as number) / m2PerPackage));
    },
    [m2PerPackage, changePkg]
  );

  const debouncedChangeM2 = useMemo(
    () => debounce(() => ref.current && changeM2(ref.current?.value), 500),
    [changeM2]
  );

  //Make it possible to update m2 value from props
  useEffect(() => {
    setM2(
      valuePkg
        ? multiply(valuePkg, m2PerPackage)
        : valueM2
        ? valueM2
        : undefined
    );
    setPkg(
      valuePkg ? valuePkg : valueM2 ? Math.ceil(valueM2 / m2PerPackage) : 0
    );
  }, [m2PerPackage, setPkg, valueM2, valuePkg]);

  const total = multiply(pkg, pricePerPackage);

  console.log("m2", m2);

  return (
    <div
      id={id}
      className={classNames("package-calculator-compact", className)}
      style={style}
    >
      {!compact ? (
        <>
          <div className={"plus-minus-box"}>
            <Button
              onClick={() => pkg > 0 && changePkg(pkg - 1)}
              disabled={pkg <= 1}
              color="white"
              className="step-button step-button-left"
            >
              <Icon name="minus" />
            </Button>
            <div className={"plus-minus-inner"}>
              <InputGroup>
                <span>{pkg + " " + tp("sales_unit.per_square_meter")}</span>
              </InputGroup>
              <InputGroup>
                <Input
                  type="text"
                  inputMode="decimal"
                  name="m2"
                  min="0"
                  max="9999"
                  pattern="^\d*(\.\d{0,2})?$"
                  defaultValue={m2 || 0}
                  onChange={() => {
                    debouncedChangeM2();
                  }}
                  onClick={e => {
                    ref.current?.select();
                    e.stopPropagation();
                  }}
                  innerRef={ref}
                />
                <InputGroupText>m&sup2;</InputGroupText>
              </InputGroup>
            </div>
            <Button
              onClick={() => changePkg(pkg + 1)}
              disabled={pkg >= stockInt}
              color="white"
              className="step-button step-button-right"
            >
              <Icon name="plus" />
            </Button>
          </div>
          {!b2b && !isBusiness && !compact && (
            <>
              <div className="price-and-stock">
                <span className="price-cell">
                  <FormattedPrice
                    price={total}
                    currency={currency}
                    price_class_name="price"
                  />
                  <span className="unit-price">
                    <FormattedPrice
                      price={pricePerM2 || 0}
                      currency={currency}
                      price_unit={priceUnit}
                    />
                  </span>
                </span>
                <div className="stock-status-cell">
                  <ProductStock product={product} b2b={b2b} short={false} />
                </div>
                {children}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <Input
              type="text"
              inputMode="decimal"
              name="m2"
              min="0"
              max="9999"
              pattern="^\d*(\.\d{0,2})?$"
              defaultValue={m2 || 0}
              onChange={() => {
                debouncedChangeM2();
              }}
              onClick={e => {
                ref.current?.select();
                e.stopPropagation();
              }}
              innerRef={ref}
              style={{ width: "4em", marginRight: "0.5rem" }}
            />
            <span>m&sup2;</span>
          </div>
          <span className="text-small" style={{ marginLeft: "0.5rem" }}>
            {pkg > 0 && "= " + pkg + " " + tp("sales_unit.per_square_meter")}
          </span>
        </>
      )}
    </div>
  );
};

CompactPackageCalculator.displayName = "CompactPackageCalculator";

export default CompactPackageCalculator;
